/*
 * COUNTERPOINT theme overrides for GH Produce (Broadcast 8.1.1).
 * theme.js is never edited; overrides live here so a theme update keeps them.
 *
 * 1. Product gallery swipe past a video slide. The theme's touchend handler
 *    only moves when the active slide is an image, so a swipe on a video
 *    slide is dropped. This adds the missing move for video slides only.
 * 2. A gallery video pauses whenever its slide stops being active, however
 *    that happens: swipe, arrow, thumbnail or variant change.
 * 3. Counterpoint 2026-10-14: the Nutritional Panel tab shows the panel for the
 *    selected size, switched on the theme's own variant change event.
 *
 * Touch only. Desktop keeps the theme arrows. Exits quietly on any page
 * without a product gallery.
 */
(function () {
  'use strict';

  var GALLERY = 'product-images';
  var LIST = '[data-product-media-list]';
  var ACTIVE = 'media--active';
  var CONTROL_BAR = 60;
  var MIN_MOVE = 10;

  var gesture = null;

  function galleryOf(target) {
    if (!target || typeof target.closest !== 'function') return null;
    var list = target.closest(LIST);
    if (!list) return null;
    var gallery = list.closest(GALLERY);
    if (!gallery) return null;
    if (typeof gallery.getCurrentMedia !== 'function') return null;
    if (typeof gallery.showNextImage !== 'function') return null;
    if (typeof gallery.showPreviousImage !== 'function') return null;
    return gallery;
  }

  function startedOnControlBar(slide, x, y) {
    var videos = slide.querySelectorAll('deferred-media[loaded] video');
    for (var i = 0; i < videos.length; i++) {
      var r = videos[i].getBoundingClientRect();
      if (x >= r.left && x <= r.right && y <= r.bottom && y >= r.bottom - CONTROL_BAR) return true;
    }
    return false;
  }

  function onTouchStart(e) {
    gesture = null;
    if (!e.touches || e.touches.length !== 1) return;
    var gallery = galleryOf(e.target);
    if (!gallery) return;
    var slide = gallery.getCurrentMedia();
    if (!slide) return;
    var t = e.touches[0];
    gesture = {
      gallery: gallery,
      slide: slide,
      x: t.clientX,
      y: t.clientY,
      onControls: startedOnControlBar(slide, t.clientX, t.clientY)
    };
  }

  function onTouchEnd(e) {
    var g = gesture;
    gesture = null;
    if (!g || !e.changedTouches || !e.changedTouches.length) return;
    var t = e.changedTouches[0];
    var dx = t.clientX - g.x;
    var dy = t.clientY - g.y;
    // Wait one frame so the theme's own touchend handler has already run.
    requestAnimationFrame(function () {
      var gallery = g.gallery;
      if (gallery.initialized !== true) return;
      if (g.slide.getAttribute('data-type') !== 'video') return;
      if (gallery.getCurrentMedia() !== g.slide) return;
      if (Math.abs(dx) <= MIN_MOVE || Math.abs(dx) <= Math.abs(dy)) return;
      if (g.onControls) return;
      if (dx < 0) {
        gallery.showNextImage();
      } else {
        gallery.showPreviousImage();
      }
    });
  }

  // A slide that loses media--active has been left: pause the playing videos
  // inside it and mark them. Only gallery slides are watched, so the background
  // video section and any other video on the page are never touched.
  // A slide that gains media--active is never paused. The theme loads and
  // autoplays a video the first time its slide opens; when a slide we paused
  // becomes active again, its video resumes. A video the shopper paused stays paused.
  var PAUSED_BY_US = 'data-counterpoint-paused';

  function onClassChange(mutations) {
    try {
      handleSlideChanges(mutations);
    } catch (err) {
      // Stay silent: this file loads on every page.
    }
  }

  function isGallerySlide(el) {
    return !!(el && el.classList && el.hasAttribute('data-image-id') && el.closest(GALLERY));
  }

  function handleSlideChanges(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      var slide = m.target;
      if (!isGallerySlide(slide)) continue;
      var wasActive = !!m.oldValue && m.oldValue.indexOf(ACTIVE) !== -1;
      var isActive = slide.classList.contains(ACTIVE);
      if (wasActive === isActive) continue;
      var videos = slide.querySelectorAll('video');
      for (var j = 0; j < videos.length; j++) {
        var v = videos[j];
        if (wasActive && !isActive && !v.paused) {
          v.setAttribute(PAUSED_BY_US, '');
          v.pause();
        } else if (isActive && v.hasAttribute(PAUSED_BY_US)) {
          v.removeAttribute(PAUSED_BY_US);
          var p = v.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        }
      }
    }
  }

  try {
    document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true, capture: true });
    document.addEventListener('touchcancel', function () { gesture = null; }, { passive: true, capture: true });
    // One observer for every gallery on the page, including galleries added
    // later (quick add popup, section reloads in the theme editor).
    if (typeof MutationObserver === 'function' && document.body) {
      new MutationObserver(onClassChange).observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true
      });
    }
  } catch (err) {
    // Stay silent: this file loads on every page.
  }
})();

/*
 * Counterpoint 2026-10-14: Nutritional Panel tab (snippets/product-tabs.liquid).
 * Every size's state is rendered into the page, each listing the variant IDs it
 * serves (and, for the line, the IDs that show it). On the theme's variantChange
 * event this shows the state for the new variant, only inside the product-info
 * whose section published the event, so another product on the page is never
 * changed. A variant ID that matches no state leaves the tab as it is.
 */
(function () {
  'use strict';

  function showVariant(root, variantId) {
    var key = ',' + variantId + ',';
    var states = root.querySelectorAll('[data-cp-nip-variants]');
    var match = null;
    var i;
    for (i = 0; i < states.length; i++) {
      if (states[i].getAttribute('data-cp-nip-variants').indexOf(key) !== -1) {
        match = states[i];
        break;
      }
    }
    if (!match) return;
    for (i = 0; i < states.length; i++) {
      states[i].hidden = states[i] !== match;
    }
    var line = match.querySelector('[data-cp-nip-line-variants]');
    if (line) line.hidden = line.getAttribute('data-cp-nip-line-variants').indexOf(key) === -1;
  }

  function onVariantChange(payload) {
    try {
      var data = payload && payload.data;
      if (!data || !data.variant || !data.variant.id || !data.sectionId) return;
      var infos = document.querySelectorAll('product-info');
      for (var i = 0; i < infos.length; i++) {
        var info = infos[i];
        var section = info.dataset.originalSection || info.dataset.section;
        if (String(section) !== String(data.sectionId)) continue;
        var roots = info.querySelectorAll('[data-cp-nip]');
        for (var j = 0; j < roots.length; j++) {
          if (roots[j].closest('product-info') === info) showVariant(roots[j], data.variant.id);
        }
      }
    } catch (err) {
      // Stay silent: this file loads on every page.
    }
  }

  try {
    if (typeof window.subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS && window.theme.PUB_SUB_EVENTS.variantChange) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, onVariantChange);
    }
  } catch (err) {
    // Stay silent: this file loads on every page.
  }
})();
